import type { DonationsProps } from '@noorview/core';
import { el, sv } from './dom';
import type { Env, WidgetView } from './widgets';

/**
 * Donations: minimal designs that show the appeal's name, how much has been raised, the target and what's left.
 * The percentage, the amount still to raise and the number of donors are each switched on or off in the Studio.
 */

const money = (n: number, cur: string) => `${cur || '£'}${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(Math.max(0, n))}`;

function figures(p: DonationsProps) {
  const raised = Math.max(0, Number(p.raised) || 0), goal = Math.max(0, Number(p.goal) || 0);
  const pct = goal > 0 ? Math.min(1, raised / goal) : 0;
  return {
    raised, goal, pct, pctText: `${Math.round(pct * 100)}%`, left: Math.max(0, goal - raised), donors: Math.max(0, Math.round(Number(p.donors) || 0)),
    showPct: p.showPercent !== false && goal > 0, showLeft: p.showRemaining !== false && goal > 0, showDonors: !!p.showDonors,
  };
}
type Figures = ReturnType<typeof figures>;
const donorsText = (n: number) => `${new Intl.NumberFormat('en-GB').format(n)} ${n === 1 ? 'donor' : 'donors'}`;

/** The appeal's name, at the top of every design (hidden when there isn't one). */
function heading(p: DonationsProps, t: HTMLElement) {
  t.textContent = p.appeal || '';
  t.style.display = p.appeal ? '' : 'none';
}

/** A line of small details: each shown only when it's switched on. */
function details(foot: HTMLElement, parts: Array<string | false>) {
  foot.replaceChildren(...parts.filter((x): x is string => !!x).map((x) => el('span', '', x)));
}

function barOf(): { bar: HTMLElement; set: (pct: number) => void } {
  const bar = el('div', 'bar'), fill = el('i');
  bar.append(fill);
  return { bar, set: (pct) => { fill.style.transform = `scaleX(${pct})`; } };
}

/** Raised so far and the target side by side, the bar, then the percentage, what's left and donors. */
export function donationBar(c: HTMLElement): WidgetView {
  const wrap = el('div', 'dn dn-bar'), t = el('div', 'dn-t');
  const row = el('div', 'dn-row'), l = el('div', 'dn-l'), r = el('div', 'dn-r');
  const raised = el('div', 'dn-v nvd-acc'), goal = el('div', 'dn-v2'), rk = el('div', 'dn-k', 'Raised so far'), gk = el('div', 'dn-k', 'Target');
  l.append(rk, raised); r.append(gk, goal); row.append(l, r);
  const b = barOf(), foot = el('div', 'dn-foot');
  wrap.append(t, row, b.bar, foot);
  c.append(wrap);
  return {
    update(w) {
      const p = w.props as DonationsProps, n = figures(p);
      heading(p, t);
      raised.textContent = money(n.raised, p.currency);
      goal.textContent = n.goal ? money(n.goal, p.currency) : '';
      r.style.display = n.goal ? '' : 'none';
      b.bar.style.display = n.goal ? '' : 'none';
      b.set(n.pct);
      details(foot, [n.showPct && `${n.pctText} of target reached`, n.showLeft && `${money(n.left, p.currency)} to go`, n.showDonors && donorsText(n.donors)]);
    },
    tick() { return false; },
    destroy() {},
  };
}

/** A loading bar of blocks that fill as money comes in, with the percentage large underneath. */
export function donationSegments(c: HTMLElement): WidgetView {
  const SEGMENTS = 20;
  const wrap = el('div', 'dn dn-segs'), t = el('div', 'dn-t'), seg = el('div', 'dn-seg'), big = el('div', 'dn-big'), foot = el('div', 'dn-foot');
  const blocks = Array.from({ length: SEGMENTS }, () => el('i'));
  seg.append(...blocks);
  wrap.append(t, seg, big, foot);
  c.append(wrap);
  return {
    update(w) {
      const p = w.props as DonationsProps, n = figures(p);
      heading(p, t);
      // A started appeal always lights at least one block.
      const on = n.raised > 0 ? Math.max(1, Math.round(n.pct * SEGMENTS)) : 0;
      blocks.forEach((b, i) => b.classList.toggle('on', i < on));
      big.textContent = n.showPct ? n.pctText : money(n.raised, p.currency);
      details(foot, [n.goal ? `${n.showPct ? `${money(n.raised, p.currency)} of ` : 'of '}${money(n.goal, p.currency)}` : 'raised so far', n.showLeft && `${money(n.left, p.currency)} to go`, n.showDonors && donorsText(n.donors)]);
    },
    tick() { return false; },
    destroy() {},
  };
}

const ICONS: Record<string, string> = {
  target: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.5"/>',
  raised: '<path d="M3 17l6-6 4 4 8-8"/><path d="M15 7h6v6"/>',
  left: '<path d="M6 3h12M6 21h12M7 3c0 5 10 6 10 9s-10 4-10 9M17 3c0 5-10 6-10 9s10 4 10 9"/>',
  donors: '<circle cx="9" cy="8" r="3.5"/><path d="M2.5 20c.5-3.6 3.2-6 6.5-6s6 2.4 6.5 6"/><path d="M16 4.6a3.5 3.5 0 0 1 0 6.8M18.5 14.3c1.7.9 2.8 2.9 3 5.7"/>',
};
function card(icon: string, label: string, hi = false) {
  const root = el('div', `dn-card${hi ? ' hi' : ''}`), ic = el('span', 'dn-ic'), v = el('div', 'dn-v'), k = el('div', 'dn-k', label);
  const s = sv('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'aria-hidden': 'true' });
  s.innerHTML = ICONS[icon]!;
  ic.append(s);
  root.append(ic, v, k);
  return { root, v };
}

/** Cards for the target, the amount raised (highlighted), what's left and donors, over a progress panel. */
export function donationCards(c: HTMLElement): WidgetView {
  const wrap = el('div', 'dn dn-cw'), t = el('div', 'dn-t'), cards = el('div', 'dn-cards');
  const goal = card('target', 'Target'), raised = card('raised', 'Raised so far', true), left = card('left', 'Still to raise'), donors = card('donors', 'Donors');
  cards.append(goal.root, raised.root, left.root, donors.root);
  const prog = el('div', 'dn-prog'), ph = el('div', 'dn-ph'), pl = el('b', '', 'Progress'), pv = el('span');
  ph.append(pl, pv);
  const b = barOf();
  prog.append(ph, b.bar);
  wrap.append(t, cards, prog);
  c.append(wrap);
  return {
    update(w, env: Env) {
      const p = w.props as DonationsProps, n = figures(p);
      heading(p, t);
      // A tall widget stacks the cards; a wide one sets them in a row.
      wrap.classList.toggle('tall', env.box.width / Math.max(1, env.box.height) < 1);
      // A small widget keeps the figures and drops the icons, so the numbers stay readable.
      wrap.classList.toggle('compact', Math.min(env.box.width, env.box.height) < 360);
      goal.v.textContent = money(n.goal, p.currency);
      goal.root.style.display = n.goal ? '' : 'none';
      raised.v.textContent = money(n.raised, p.currency);
      left.v.textContent = money(n.left, p.currency);
      left.root.style.display = n.showLeft ? '' : 'none';
      donors.v.textContent = new Intl.NumberFormat('en-GB').format(n.donors);
      donors.root.style.display = n.showDonors ? '' : 'none';
      prog.style.display = n.goal ? '' : 'none';
      pv.textContent = n.showPct ? n.pctText : '';
      pv.style.display = n.showPct ? '' : 'none';
      b.set(n.pct);
    },
    tick() { return false; },
    destroy() {},
  };
}

/** A ring that fills to the target, with the percentage (or the amount) in the middle. */
export function donationRing(c: HTMLElement): WidgetView {
  const wrap = el('div', 'ring-w');
  const svg = sv('svg', { viewBox: '0 0 200 200', class: 'ring', 'aria-hidden': 'true' });
  const arc = sv('circle', { cx: 100, cy: 100, r: 88, class: 'ring-arc' });
  svg.append(sv('circle', { cx: 100, cy: 100, r: 88, class: 'ring-track' }), arc);
  const inner = el('div', 'ring-in'), t = el('div', 'nvd-label'), big = el('div', 'big'), sub = el('div', 'nvd-sub'), more = el('div', 'nvd-sub nvd-faint');
  inner.append(t, big, sub, more);
  wrap.append(svg, inner);
  c.append(wrap);
  const C = 2 * Math.PI * 88;
  arc.setAttribute('stroke-dasharray', `${C} ${C}`);
  return {
    update(w, env: Env) {
      const p = w.props as DonationsProps, n = figures(p);
      heading(p, t);
      big.textContent = n.showPct ? n.pctText : money(n.raised, p.currency);
      sub.textContent = n.goal ? `${n.showPct ? `${money(n.raised, p.currency)} of ` : 'of '}${money(n.goal, p.currency)}` : 'raised so far';
      const extra = [n.showLeft && `${money(n.left, p.currency)} to go`, n.showDonors && donorsText(n.donors)].filter(Boolean).join(' · ');
      // A small ring has room for the percentage and amounts only: the extra line would be too small to read.
      const room = Math.min(env.box.width, env.box.height) >= 360;
      more.textContent = extra;
      more.style.display = extra && room ? '' : 'none';
      arc.style.strokeDashoffset = String(C * (1 - n.pct));
    },
    tick() { return false; },
    destroy() {},
  };
}

/** Just the numbers: the amount raised large, the target under it, then the details. */
export function donationNumbers(c: HTMLElement): WidgetView {
  const wrap = el('div', 'dn dn-num'), t = el('div', 'dn-t'), big = el('div', 'big nvd-acc'), of = el('div', 'dn-k dn-of'), foot = el('div', 'dn-foot');
  wrap.append(t, big, of, foot);
  c.append(wrap);
  return {
    update(w) {
      const p = w.props as DonationsProps, n = figures(p);
      heading(p, t);
      big.textContent = money(n.raised, p.currency);
      of.textContent = n.goal ? `raised of ${money(n.goal, p.currency)}` : 'raised so far';
      details(foot, [n.showPct && `${n.pctText} reached`, n.showLeft && `${money(n.left, p.currency)} to go`, n.showDonors && donorsText(n.donors)]);
    },
    tick() { return false; },
    destroy() {},
  };
}

/** A half-circle gauge with a needle, from nothing on the left to the target on the right. */
export function donationGauge(c: HTMLElement): WidgetView {
  const wrap = el('div', 'dg');
  const svg = sv('svg', { viewBox: '0 0 220 136', class: 'dg-svg', 'aria-hidden': 'true' });
  const path = 'M20 112 A90 90 0 0 1 200 112';
  const arc = sv('path', { d: path, class: 'dg-arc' });
  const needle = sv('g', { class: 'dg-needle' }, sv('line', { x1: 110, y1: 112, x2: 110, y2: 36 }), sv('circle', { cx: 110, cy: 112, r: 8 }));
  svg.append(sv('path', { d: path, class: 'dg-track' }), arc);
  for (let i = 0; i <= 10; i++) {
    const a = Math.PI * (1 - i / 10), r0 = i % 5 ? 70 : 64, r1 = 76;
    svg.append(sv('line', { x1: 110 + r0 * Math.cos(a), y1: 112 - r0 * Math.sin(a), x2: 110 + r1 * Math.cos(a), y2: 112 - r1 * Math.sin(a), class: i % 5 ? 'dg-tk' : 'dg-tk major' }));
  }
  const e0 = sv('text', { x: 20, y: 132, class: 'dg-end', 'text-anchor': 'middle' }), e1 = sv('text', { x: 200, y: 132, class: 'dg-end', 'text-anchor': 'middle' });
  svg.append(needle, e0, e1);
  const meta = el('div', 'dg-meta'), t = el('div', 'dn-t'), big = el('div', 'big'), sub = el('div', 'dn-k'), foot = el('div', 'dn-foot');
  meta.append(t, big, sub, foot);
  wrap.append(svg, meta);
  c.append(wrap);
  const L = Math.PI * 90;
  arc.setAttribute('stroke-dasharray', `${L} ${L}`);
  return {
    update(w) {
      const p = w.props as DonationsProps, n = figures(p);
      heading(p, t);
      big.textContent = n.showPct ? n.pctText : money(n.raised, p.currency);
      sub.textContent = n.goal ? `${n.showPct ? `${money(n.raised, p.currency)} raised` : 'raised'}` : 'raised so far';
      details(foot, [n.showLeft && `${money(n.left, p.currency)} to go`, n.showDonors && donorsText(n.donors)]);
      e0.textContent = money(0, p.currency);
      e1.textContent = n.goal ? money(n.goal, p.currency) : '';
      arc.style.strokeDashoffset = String(L * (1 - n.pct));
      (needle as SVGGElement).style.transform = `rotate(${(-90 + 180 * n.pct).toFixed(2)}deg)`;
    },
    tick() { return false; },
    destroy() {},
  };
}

export type { Figures };
